import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard } from '@fortawesome/free-solid-svg-icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { operationType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { webURL } from '../../../../../services/API';
import { RootState } from '../../../../../redux/store';
import { ExpandedBlock } from '../../../expanded';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { AuthenticateSelect } from '../../../../auth/select';
import { Main, Styled } from './style';

type OdooTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

type CapabilityStatus = 'supported' | 'unsupported' | 'unknown_permission';

type TopicDefinition = {
  value: string;
  label: string;
  group: string;
  hint: string;
};

const TOPICS: TopicDefinition[] = [
  { value: 'order.created', label: 'Order Created', group: 'sales', hint: 'New sale order detected' },
  { value: 'order.updated', label: 'Order Updated', group: 'sales', hint: 'Existing order changed' },
  { value: 'order.confirmed', label: 'Order Confirmed', group: 'sales', hint: 'Quotation moved to sale' },
  { value: 'order.cancelled', label: 'Order Cancelled', group: 'sales', hint: 'Order moved to cancel' },
  { value: 'order.delivered', label: 'Order Delivered', group: 'sales', hint: 'Related shipment done' },
  { value: 'order.paid', label: 'Order Paid', group: 'sales', hint: 'Related invoice paid' },
  { value: 'customer.created', label: 'Customer Created', group: 'customers', hint: 'New partner/customer record' },
  { value: 'customer.updated', label: 'Customer Updated', group: 'customers', hint: 'Customer profile changed' },
  { value: 'product.created', label: 'Product Created', group: 'products', hint: 'New product template' },
  { value: 'product.updated', label: 'Product Updated', group: 'products', hint: 'Product details changed' },
  { value: 'product.archived', label: 'Product Archived', group: 'products', hint: 'Product deactivated' },
  { value: 'inventory.stock_increased', label: 'Stock Increased', group: 'inventory', hint: 'Done movement into scoped stock' },
  { value: 'inventory.stock_decreased', label: 'Stock Decreased', group: 'inventory', hint: 'Done movement out of scoped stock' },
  { value: 'inventory.low_stock', label: 'Low Stock', group: 'inventory', hint: 'Quantity at or below threshold' },
  { value: 'inventory.out_of_stock', label: 'Out Of Stock', group: 'inventory', hint: 'Quantity at or below zero' },
  { value: 'invoice.created', label: 'Invoice Created', group: 'invoices', hint: 'New account.move invoice' },
  { value: 'invoice.paid', label: 'Invoice Paid', group: 'invoices', hint: 'Payment state became paid' },
  { value: 'invoice.cancelled', label: 'Invoice Cancelled', group: 'invoices', hint: 'Invoice moved to cancel' },
  { value: 'website.cart_created', label: 'Cart Created', group: 'website', hint: 'Website sale cart draft created' },
  { value: 'website.checkout_started', label: 'Checkout Started', group: 'website', hint: 'Cart advanced toward checkout' },
  { value: 'website.order_completed', label: 'Order Completed', group: 'website', hint: 'Website order confirmed' },
  { value: 'website.new_lead', label: 'New Website Lead', group: 'website', hint: 'Website lead captured' },
  { value: 'shipment.created', label: 'Shipment Created', group: 'delivery', hint: 'New stock picking created' },
  { value: 'shipment.assigned', label: 'Shipment Assigned', group: 'delivery', hint: 'Picking reserved and assigned' },
  { value: 'shipment.delivered', label: 'Shipment Delivered', group: 'delivery', hint: 'Picking moved to done' },
  { value: 'shipment.cancelled', label: 'Shipment Cancelled', group: 'delivery', hint: 'Picking moved to cancel' },
];

const DEFAULT_TOPIC = 'order.created';

function getWebhookUrlIssue(urlValue?: string) {
  const value = (urlValue || '').trim();
  if (!value) return '';
  try {
    const parsed = new URL(value);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return 'Webhook URL must start with http:// or https://.';
    }
    return '';
  } catch {
    return 'Generated webhook URL is invalid. Check MAIN_Server_DOMAIN and regenerate.';
  }
}

function normalizeTopics(eventTopics: unknown, eventTopic?: string): string[] {
  const allowed = new Set(TOPICS.map((topic) => topic.value));
  const fromArray = Array.isArray(eventTopics)
    ? eventTopics
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter((item) => item.length > 0 && allowed.has(item))
    : [];

  if (fromArray.length) {
    return Array.from(new Set(fromArray));
  }

  const single = typeof eventTopic === 'string' ? eventTopic.trim() : '';
  return single && allowed.has(single) ? [single] : [];
}

function normalizeDomainText(filters: any) {
  if (Array.isArray(filters?.domain)) {
    return JSON.stringify(filters.domain, null, 2);
  }
  return '';
}

export function OdooTrigger({ id, data, setChildMinimize, setChildSettings }: OdooTriggerProps) {
  const { setNodes } = useReactFlow();
  const [searchParams] = useSearchParams();
  const userToken = useSelector((state: RootState) => state.user.data?.token);
  const projectId = searchParams.get('id');

  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isCopy, setIsCopy] = React.useState(false);
  const [link, setLink] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [capabilities, setCapabilities] = React.useState<any>(data.value?.capabilities || null);
  const [domainText, setDomainText] = React.useState<string>(normalizeDomainText(data.value?.filters));
  const [topicsOpen, setTopicsOpen] = React.useState(true);
  const [pollingOpen, setPollingOpen] = React.useState(true);
  const [webhookOpen, setWebhookOpen] = React.useState(false);
  const [unavailableOpen, setUnavailableOpen] = React.useState(false);

  const selectedTopics = React.useMemo(
    () => normalizeTopics(data.value?.eventTopics, data.value?.eventTopic),
    [data.value?.eventTopics, data.value?.eventTopic]
  );
  const activeTopic = selectedTopics[0] || DEFAULT_TOPIC;
  const webhookUrlIssue = React.useMemo(() => getWebhookUrlIssue(link), [link]);

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => { setIsExpanded((prev) => !prev); });
    if (setChildSettings) setChildSettings(() => { setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle })); });
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (data?.triggerCredentials?.webhookUrl) {
      setLink(data.triggerCredentials.webhookUrl);
    }
  }, [data?.triggerCredentials?.webhookUrl]);

  React.useEffect(() => {
    setDomainText(normalizeDomainText(data.value?.filters));
  }, [data.value?.filters]);

  React.useEffect(() => {
    const nextTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
    if (nextTopics.length === 0) {
      updateNodeValue(id, setNodes, { eventTopics: [DEFAULT_TOPIC], eventTopic: DEFAULT_TOPIC });
      return;
    }

    if (!Array.isArray(data.value?.eventTopics) || data.value?.eventTopic !== nextTopics[0]) {
      updateNodeValue(id, setNodes, { eventTopics: nextTopics, eventTopic: nextTopics[0] });
    }
  }, [data.value?.eventTopics, data.value?.eventTopic, id, setNodes]);

  const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
    if (type === 'add') {
      updateNodeValue(id, setNodes, { connection: connectionData });
      setCapabilities(null);
      setError('');
      setSuccess('');
    } else {
      updateNodeValue(id, setNodes, { connection: null, capabilities: null });
      setCapabilities(null);
    }
  };

  const fetchCapabilities = React.useCallback(async () => {
    if (!userToken || !data.value?.connection?.id) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${webURL}/triggers/odoo/capabilities`,
        { connectionId: data.value.connection.id },
        { headers: { 'x-auth-token': userToken } }
      );

      setCapabilities(response.data.capabilities);
      updateNodeValue(id, setNodes, { capabilities: response.data.capabilities });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load Odoo capabilities');
    } finally {
      setIsLoading(false);
    }
  }, [data.value?.connection?.id, id, setNodes, userToken]);

  React.useEffect(() => {
    if (isExpanded && data.value?.connection?.id) {
      void fetchCapabilities();
    }
  }, [fetchCapabilities, isExpanded, data.value?.connection?.id]);

  const handleToggleTopic = (topic: string) => {
    const currentTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
    const nextTopics = currentTopics.includes(topic)
      ? currentTopics.filter((item) => item !== topic)
      : [...currentTopics, topic];

    updateNodeValue(id, setNodes, {
      eventTopics: nextTopics.length ? nextTopics : [topic],
      eventTopic: (nextTopics[0] || topic),
    });
    setSuccess('');
  };

  const updatePollingField = (field: string, value: any) => {
    updateNodeValue(id, setNodes, { [field]: value });
  };

  const persistDomainFilter = () => {
    const trimmed = domainText.trim();
    if (!trimmed) {
      const nextFilters = { ...(data.value?.filters || {}) };
      delete nextFilters.domain;
      updateNodeValue(id, setNodes, { filters: nextFilters });
      setError('');
      return;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        throw new Error('Domain JSON must be an array');
      }
      updateNodeValue(id, setNodes, {
        filters: {
          ...(data.value?.filters || {}),
          domain: parsed,
        },
      });
      setError('');
    } catch (err: any) {
      setError(err.message || 'Domain JSON is invalid');
    }
  };

  const postAction = async (path: string, payload: Record<string, any>) => {
    if (!projectId) throw new Error('Missing project id');
    if (!userToken) throw new Error('Missing auth token');
    if (!data.value?.connection?.id) throw new Error('Please select an Odoo connection first');

    const response = await axios.post(
      `${webURL}/triggers/odoo/${path}`,
      {
        projectId,
        nodeIndex: data.index,
        connectionId: data.value.connection.id,
        eventTopic: activeTopic,
        eventTopics: selectedTopics,
        ...payload,
      },
      { headers: { 'x-auth-token': userToken } }
    );

    return response.data;
  };

  const handleGenerateLink = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await postAction('generate-link', {});
      setCapabilities(response.capabilities || capabilities);
      setLink(response.webhookUrl || '');
      setNodes((prev) =>
        prev.map((node) =>
          node.data.index === data.index
            ? {
                ...node,
                data: {
                  ...node.data,
                  value: {
                    ...node.data.value,
                    capabilities: response.capabilities || node.data.value?.capabilities,
                  },
                  triggerCredentials: {
                    nodeId: response.nodeId,
                    webhookUrl: response.webhookUrl,
                    blockId: response.blockId,
                  },
                },
              }
            : node
        )
      );
      setSuccess('Webhook link generated and trigger state synced.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to generate Odoo trigger link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await postAction('activate', {});
      setCapabilities(response.capabilities || capabilities);
      setSuccess('Odoo trigger activated.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to activate Odoo trigger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!projectId || !userToken) return;
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(
        `${webURL}/triggers/odoo/deactivate`,
        { projectId, nodeIndex: data.index },
        { headers: { 'x-auth-token': userToken } }
      );
      setSuccess('Odoo trigger deactivated.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to deactivate Odoo trigger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestEvent = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await postAction('test-event', { topic: activeTopic });
      updateNodeValue(id, setNodes, { lastTestEvent: response.payload });
      setSuccess(`Sent real Odoo test event for ${activeTopic}.`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send Odoo test event');
    } finally {
      setIsLoading(false);
    }
  };

  const groupedTopics = React.useMemo(() => {
    const groups = new Map<string, TopicDefinition[]>();
    for (const topic of TOPICS) {
      if (!groups.has(topic.group)) groups.set(topic.group, []);
      groups.get(topic.group)?.push(topic);
    }
    return Array.from(groups.entries()).map(([group, topics]) => ({ group, topics }));
  }, []);

  const availableGroups = groupedTopics.filter(({ topics }) =>
    topics.some((topic) => (capabilities?.topics?.[topic.value] || 'supported') !== 'unsupported')
  );
  const unavailableGroups = groupedTopics.filter(({ topics }) =>
    topics.every((topic) => (capabilities?.topics?.[topic.value] || 'supported') === 'unsupported')
  );

  const isWarning = !data.value?.connection || selectedTopics.length === 0;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '540px' }}
          >
              <Styled>
                <div className="action">
                  <AuthenticateSelect
                    type="odoo"
                    defaultId={data.value?.connection?.id}
                    handleAuthenticate={handleAuthenticate}
                    background={data.background}
                  />
                </div>

                <div className="statusRow">
                  <span className={`statusBadge ${link ? 'ready' : 'missing'}`}>Webhook {link ? 'Ready' : 'Missing'}</span>
                  <span className={`statusBadge ${capabilities?.version ? 'ready' : 'unknown'}`}>Version {capabilities?.version?.server_version || 'Unknown'}</span>
                  <span className={`statusBadge ${selectedTopics.length ? 'ready' : 'missing'}`}>Topics {selectedTopics.length}</span>
                  {isLoading ? <span className="statusBadge loading">Loading Odoo trigger...</span> : null}
                </div>

                <div className="sectionCard">
                  <button type="button" className="sectionToggle" onClick={() => setTopicsOpen((prev) => !prev)}>
                    <span>Supported Trigger Groups</span>
                    <span className="sectionToggleIcon">{topicsOpen ? '-' : '+'}</span>
                  </button>
                  {topicsOpen ? (
                    <div className="sectionBody">
                      {availableGroups.map(({ group, topics }) => (
                        <div key={group} className="groupCard">
                          <div className="groupHeader">
                            <span className="groupTitle">{group}</span>
                            <span className={`statusBadge ${(capabilities?.groups?.[group] || 'unknown_permission') === 'supported' ? 'ready' : 'unknown'}`}>
                              {capabilities?.groups?.[group] || 'pending'}
                            </span>
                          </div>
                          <div className="topicChecklist">
                            {topics.map((topic) => {
                              const status = (capabilities?.topics?.[topic.value] || 'supported') as CapabilityStatus;
                              const disabled = status !== 'supported';
                              const checked = selectedTopics.includes(topic.value);
                              return (
                                <label key={topic.value} className={`topicItem ${disabled ? 'disabled' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => handleToggleTopic(topic.value)}
                                  />
                                  <span className="topicMeta">
                                    <span className="topicLabel">{topic.label}</span>
                                    <span className="topicHint">{topic.hint}</span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {unavailableGroups.length ? (
                  <div className="sectionCard">
                    <button type="button" className="sectionToggle" onClick={() => setUnavailableOpen((prev) => !prev)}>
                      <span>Unavailable Groups</span>
                      <span className="sectionToggleIcon">{unavailableOpen ? '-' : '+'}</span>
                    </button>
                    {unavailableOpen ? (
                      <div className="sectionBody">
                        {unavailableGroups.map(({ group }) => (
                          <div key={group} className="groupCard">
                            <div className="groupHeader">
                              <span className="groupTitle">{group}</span>
                              <span className="statusBadge missing">{capabilities?.groups?.[group] || 'unsupported'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="sectionCard">
                  <button type="button" className="sectionToggle" onClick={() => setPollingOpen((prev) => !prev)}>
                    <span>Polling Settings</span>
                    <span className="sectionToggleIcon">{pollingOpen ? '-' : '+'}</span>
                  </button>
                  {pollingOpen ? (
                    <div className="sectionBody">
                      <div className="settingsGrid">
                        <div className="field">
                          <label>Poll Interval</label>
                          <input
                            type="number"
                            min={30}
                            value={data.value?.pollIntervalSeconds || 60}
                            onChange={(event) => updatePollingField('pollIntervalSeconds', Number(event.target.value || 60))}
                          />
                        </div>
                        <div className="field">
                          <label>Batch Size</label>
                          <input
                            type="number"
                            min={1}
                            max={200}
                            value={data.value?.batchSize || 50}
                            onChange={(event) => updatePollingField('batchSize', Number(event.target.value || 50))}
                          />
                        </div>
                        <div className="field">
                          <label>Low Stock Threshold</label>
                          <input
                            type="number"
                            min={0}
                            value={data.value?.lowStockThreshold || 0}
                            onChange={(event) => updatePollingField('lowStockThreshold', Number(event.target.value || 0))}
                          />
                        </div>
                        <div className="field">
                          <label>Quantity Basis</label>
                          <select
                            value={data.value?.quantityBasis || 'available'}
                            onChange={(event) => updatePollingField('quantityBasis', event.target.value)}
                          >
                            <option value="available">Sellable Quantity</option>
                            <option value="on_hand">On-hand Quantity</option>
                          </select>
                        </div>
                  {/* 
                  
                        <div className="field full">
                          <label>Optional Odoo Domain JSON</label>
                          <textarea
                            value={domainText}
                            placeholder='[["company_id","=",1]]'
                            onChange={(event) => setDomainText(event.target.value)}
                            onBlur={persistDomainFilter}
                          />
                          <span className="fieldHint">Use fields available on the selected Odoo model. For sale.order use [["state","=","cancel"]], not [["active","=",false]]; active is valid for product.template archive filters.</span>
                        </div>
                        */}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="toolbar">
                  <button className="primaryBtn" onClick={() => void fetchCapabilities()} disabled={!data.value?.connection}>
                    Refresh Capabilities
                  </button>
           {/*
            
            
              <button className="primaryBtn" onClick={() => void handleGenerateLink()} disabled={!data.value?.connection || selectedTopics.length === 0}>
                    Generate Link
                  </button>
                  */} 
                  <button className="ghostBtn" onClick={() => void handleActivate()} disabled={!data.value?.connection || selectedTopics.length === 0}>
                    Activate
                  </button>
                  <button className="ghostBtn" onClick={() => void handleDeactivate()} disabled={!data.triggerCredentials?.nodeId}>
                    Deactivate
                  </button>
                  <button className="ghostBtn" onClick={() => void handleTestEvent()} disabled={!data.value?.connection || selectedTopics.length === 0}>
                    Test Event
                  </button>
                </div>
{/* 

                <div className="sectionCard">
                  <button type="button" className="sectionToggle" onClick={() => setWebhookOpen((prev) => !prev)}>
                    <span>Webhook Intake</span>
                    <span className="sectionToggleIcon">{webhookOpen ? '-' : '+'}</span>
                  </button>
                  {webhookOpen ? (
                    <div className="sectionBody">
                      {link ? (
                        <CopyToClipboard
                          text={link}
                          onCopy={() => {
                            setIsCopy(true);
                            setTimeout(() => setIsCopy(false), 500);
                          }}
                        >
                          <div className="link-container" title={link}>
                            <div className="link-with-copy">
                              <FontAwesomeIcon icon={isCopy ? faCheck : faClipboard} className={`copy-icon ${isCopy ? 'copied' : ''}`} />
                              <span className="link-text">{link}</span>
                            </div>
                          </div>
                        </CopyToClipboard>
                      ) : (
                        <div className="warning">Generate webhook URL first.</div>
                      )}
                      {webhookUrlIssue ? <div className="warning">{webhookUrlIssue}</div> : null}
                      <div className="fieldHint" style={{ marginTop: 8 }}>
                        Use this URL from an Odoo Automated Action or server action if you want push-style delivery. Polling and webhook payloads stay normalized the same way.
                      </div>
                    </div>
                  ) : null}
                </div>
*/}
                {isWarning ? <div className="warning">Select an Odoo connection and at least one supported topic to continue.</div> : null}
                {error ? <div className="error">{error}</div> : null}
                {success ? <div className="success">{success}</div> : null}

                <div className="instructions-section">
                  <h5>Setup Notes</h5>
                  <ol className="steps-list">
                    <li>Connect an existing Odoo connection that already works with the Odoo action node.</li>
                    <li>Refresh capabilities so the node can detect installed modules and the Odoo version profile.</li>
                    <li>Select only the supported trigger topics you want this workflow to listen for.</li>
                    <li>Use polling settings for steady background detection, and add the webhook URL in Odoo only when you want faster push delivery.</li>
                    <li>Run a test event to fetch a real matching Odoo record before turning the workflow on.</li>
                  </ol>
                </div>
              </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
